import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { userAPI, helperAPI } from '../services/api';
import AddressManager from '../components/AddressManager';
import './ProfileEdit.css';

const ProfileEdit = () => {
  const { user, updateUser } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    location: '',
    bio: '',
    services: [],
    hourlyRate: 0,
    availability: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');

  const serviceOptions = [
    { value: 'sweeping-mopping', label: '🧹 Sweeping & Mopping' },
    { value: 'cooking', label: '👨‍🍳 Cooking' },
    { value: 'laundry', label: '👕 Laundry' },
    { value: 'gardening', label: '🌱 Gardening' },
    { value: 'pet-care', label: '🐕 Pet Care' },
    { value: 'elderly-care', label: '👴 Elderly Care' },
    { value: 'babysitting', label: '👶 Babysitting' },
    { value: 'hair-dresser', label: '✂️ Hair Dresser' },
    { value: 'other', label: '🔧 Other' }
  ];

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        location: user.location || '',
        bio: user.bio || '',
        services: user.services || [],
        hourlyRate: user.hourlyRate || 0,
        availability: user.availability || {
          monday: false,
          tuesday: false,
          wednesday: false,
          thursday: false,
          friday: false,
          saturday: false,
          sunday: false
        }
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('availability.')) {
      const day = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        availability: {
          ...prev.availability,
          [day]: checked
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleServiceChange = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setError('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }

      setProfilePhoto(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      let updatedData = { ...formData };

      // Handle photo upload if there's a new photo
      if (profilePhoto) {
        // In a real app, you'd upload to a cloud service like AWS S3, Cloudinary, etc.
        // For now, we'll just simulate the photo upload
        console.log('Photo would be uploaded:', profilePhoto);
        // updatedData.profilePhoto = uploadedPhotoUrl;
      }

      // Update profile based on user role
      if (user.role === 'helper') {
        await helperAPI.updateHelperProfile(updatedData);
      } else {
        await userAPI.updateProfile(updatedData);
      }

      // Update user context
      updateUser({ ...user, ...updatedData });
      
      setSuccess('Profile updated successfully!');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const getDayLabel = (day) => {
    const labels = {
      monday: 'Mon',
      tuesday: 'Tue',
      wednesday: 'Wed',
      thursday: 'Thu',
      friday: 'Fri',
      saturday: 'Sat',
      sunday: 'Sun'
    };
    return labels[day] || day;
  };

  return (
    <div className="profile-edit-container">
      <div className="profile-edit-header">
        <h1>📝 Edit Profile</h1>
        <p>Update your profile information and preferences</p>
      </div>

      <form onSubmit={handleSubmit} className="profile-form">
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Profile Photo Section */}
        <div className="form-section">
          <h3>📸 Profile Photo</h3>
          <div className="photo-upload-section">
            <div className="current-photo">
              {photoPreview ? (
                <img src={photoPreview} alt="Profile preview" className="photo-preview" />
              ) : (
                <div className="photo-placeholder">
                  {user?.name?.charAt(0)?.toUpperCase() || '?'}
                </div>
              )}
            </div>
            <div className="photo-controls">
              <input
                type="file"
                id="profilePhoto"
                accept="image/*"
                onChange={handlePhotoChange}
                className="file-input"
              />
              <label htmlFor="profilePhoto" className="upload-btn">
                📷 Choose Photo
              </label>
              <small className="photo-hint">
                Supported formats: JPG, PNG, GIF. Max size: 5MB
              </small>
            </div>
          </div>
        </div>

        {/* Basic Information */}
        <div className="form-section">
          <h3>👤 Basic Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Full Name *</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Email Address *</label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                disabled
                className="form-input disabled"
              />
              <small className="form-hint">Email cannot be changed</small>
            </div>

            <div className="form-group full-width">
              <label htmlFor="location">Location *</label>
              <input
                type="text"
                id="location"
                name="location"
                value={formData.location}
                onChange={handleInputChange}
                required
                placeholder="Enter your city or area"
                className="form-input"
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="bio">
                Bio 
                <span className="char-count">({formData.bio.length}/500)</span>
              </label>
              <textarea
                id="bio"
                name="bio"
                value={formData.bio}
                onChange={handleInputChange}
                placeholder="Tell others about yourself, your experience, and what makes you special..."
                maxLength={500}
                rows={4}
                className="form-textarea"
              />
            </div>
          </div>
        </div>

        {/* Helper-specific fields */}
        {user?.role === 'helper' && (
          <>
            {/* Services */}
            <div className="form-section">
              <h3>🛠️ Services Offered</h3>
              <div className="services-grid">
                {serviceOptions.map(service => (
                  <label key={service.value} className="service-checkbox">
                    <input
                      type="checkbox"
                      checked={formData.services.includes(service.value)}
                      onChange={() => handleServiceChange(service.value)}
                    />
                    <span className="service-checkmark">{service.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Hourly Rate */}
            <div className="form-section">
              <h3>💰 Hourly Rate</h3>
              <div className="form-group">
                <label htmlFor="hourlyRate">Rate per hour (₹) *</label>
                <input
                  type="number"
                  id="hourlyRate"
                  name="hourlyRate"
                  value={formData.hourlyRate}
                  onChange={handleInputChange}
                  min="0"
                  step="10"
                  required
                  className="form-input"
                />
              </div>
            </div>

            {/* Availability */}
            <div className="form-section">
              <h3>📅 Availability</h3>
              <div className="availability-grid">
                {Object.keys(formData.availability).map(day => (
                  <label key={day} className="availability-day">
                    <input
                      type="checkbox"
                      name={`availability.${day}`}
                      checked={formData.availability[day]}
                      onChange={handleInputChange}
                    />
                    <span className="day-label">{getDayLabel(day)}</span>
                    <span className="day-status">
                      {formData.availability[day] ? '✅' : '❌'}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <button
            type="submit"
            disabled={loading}
            className="submit-btn"
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Updating...
              </>
            ) : (
              'Update Profile'
            )}
          </button>
        </div>
      </form>
      
      {/* Address Management Section (for customers) */}
      {user?.role === 'customer' && (
        <div className="form-section addresses-section">
          <h3>📍 Manage Saved Addresses</h3>
          <AddressManager mode="manage" />
        </div>
      )}
    </div>
  );
};

export default ProfileEdit; 